// apps/web/src/features/kronos/timer/FloatingTimerBar.tsx
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../core/theme/theme';

interface FloatingTimerBarProps {
  visible: boolean;
  projectLabel: string;
  designationLabel: string;
  autoStopLabel: string | null;
  elapsedMs: number;
  status: 'running' | 'paused';
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onNavigate: () => void;
}

function formatCounter(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default function FloatingTimerBar({
  visible,
  projectLabel,
  designationLabel,
  autoStopLabel,
  elapsedMs,
  status,
  onPause,
  onResume,
  onStop,
  onNavigate,
}: FloatingTimerBarProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!visible) return null;

  const isRunning = status === 'running';

  return (
    <Box
      onClick={onNavigate}
      sx={{
        position: 'fixed',
        bottom: {
          xs: 'calc(64px + env(safe-area-inset-bottom))', // above BottomTabBar on mobile
          md: 0, // flush with the bottom on tablet/desktop (Sidebar offsets the left edge)
        },
        left: { xs: 0, md: '64px', lg: '256px' },
        right: 0,
        height: { xs: 56, md: 48 },
        bgcolor: 'rgba(237, 200, 163, 0.95)',
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 2,
        cursor: 'pointer',
        zIndex: 1200,
        transition: 'left 0.2s ease',
      }}
      data-testid="floating-timer-bar"
    >
      {/* Pulsing dot */}
      <Box
        sx={{
          width: 8,
          height: 8,
          bgcolor: colors.darkBg,
          flexShrink: 0,
          ...(isRunning && {
            animation: 'floatingPulse 1.5s ease-in-out infinite',
          }),
          '@keyframes floatingPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        }}
      />

      {/* Project + designation + auto-stop (hidden on mobile) */}
      {!isMobile && (
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {projectLabel}
          </Typography>
          {designationLabel && (
            <Typography variant="body2" color="text.secondary" noWrap>
              — {designationLabel}
            </Typography>
          )}
          {autoStopLabel && (
            <Typography variant="caption" sx={{ ml: 'auto', opacity: 0.7 }} noWrap>
              {autoStopLabel}
            </Typography>
          )}
        </Box>
      )}

      {/* Counter */}
      <Typography
        sx={{
          fontWeight: 900,
          fontVariantNumeric: 'tabular-nums',
          fontSize: 16,
          fontFamily: 'monospace, Gabarito',
          ...(isMobile && { flex: 1 }),
        }}
      >
        {formatCounter(elapsedMs)}
      </Typography>

      {/* Buttons */}
      <Box
        sx={{ display: 'flex', gap: 0.5 }}
        onClick={(e) => e.stopPropagation()} // prevent navigate on button clicks
      >
        {isRunning ? (
          <IconButton
            size="small"
            onClick={onPause}
            aria-label={t('timer.pause')}
            sx={{ bgcolor: colors.darkBg, color: colors.white, '&:hover': { bgcolor: '#2a2d33' } }}
          >
            <PauseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={onResume}
            aria-label={t('timer.resume')}
            sx={{ bgcolor: colors.darkBg, color: colors.white, '&:hover': { bgcolor: '#2a2d33' } }}
          >
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={onStop}
          aria-label={t('timer.stop')}
          sx={{ bgcolor: colors.darkBg, color: colors.white, '&:hover': { bgcolor: '#2a2d33' } }}
        >
          <StopIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
